import { Link, useParams } from "react-router-dom"
import StepsHeroSection from "../components/landing/StepsHeroSection"
import FaqAccordion from "../components/landing/FaqAccordion"
import Footer from "../components/ui/Footer"
import { faqCategories } from "../data/faq"
import { useSiteContent } from "../hooks/useSiteContent"
import EditableText from "../components/ui/EditableText"

export default function FaqDetailPage() {
  const { category = "" } = useParams()
  const site = useSiteContent("faq")
  const faqDefault = faqCategories.find((c) => c.key === category)

  const faq = faqDefault && {
    heroHeading: site.get(
      `faq.category.${faqDefault.key}.heroHeading`,
      `${faqDefault.title.toUpperCase()} FAQ`
    ),
    summary: site.get(`faq.category.${faqDefault.key}.summary`, faqDefault.summary),
    items: faqDefault.items.map((item, index) => ({
      question: site.get(
        `faq.category.${faqDefault.key}.item.${index}.question`,
        item.question
      ),
      answer: site.get(
        `faq.category.${faqDefault.key}.item.${index}.answer`,
        item.answer
      ),
    })),
  }

  return (
    <div className="flex min-h-screen flex-col">
      {site.isAdmin && (
        <button
          type="button"
          onClick={() => site.setEditMode(!site.editMode)}
          className={`fixed bottom-6 right-6 z-50 rounded-full border-2 border-white px-5 py-3 text-sm font-semibold shadow-xl transition ${
            site.editMode ? "bg-orange-500 text-white" : "bg-dantzBlue text-white"
          }`}
        >
          {site.editMode ? "Done editing" : "Edit page"}
        </button>
      )}

      <StepsHeroSection
        heading={faq ? faq.heroHeading : "FAQ"}
        description={
          faq ? faq.summary : "The FAQ category you are looking for could not be found."
        }
        editable={faq ? site.editMode : false}
        onSaveHeading={(value) =>
          faqDefault && site.save(`faq.category.${faqDefault.key}.heroHeading`, value)
        }
        onSaveDescription={(value) =>
          faqDefault && site.save(`faq.category.${faqDefault.key}.summary`, value)
        }
      />

      <section className="bg-white px-4 py-16 md:px-8">
        <div className="mx-auto max-w-4xl text-dantzBlue">
          <div className="mx-auto w-11/12 border-t border-dantzBlue" />

          {faq ? (
            <FaqAccordion
              items={faq.items}
              editable={site.editMode}
              onSaveQuestion={(index, value) =>
                site.save(`faq.category.${faqDefault!.key}.item.${index}.question`, value)
              }
              onSaveAnswer={(index, value) =>
                site.save(`faq.category.${faqDefault!.key}.item.${index}.answer`, value)
              }
            />
          ) : (
            <p className="mt-12 text-center text-sm text-gray-600">
              Please return to the{" "}
              <Link to="/faq" className="text-dantzBlue underline">
                FAQ overview
              </Link>
              .
            </p>
          )}

          <div className="mt-16 border-t border-dantzBlue pt-10 text-center">
            <EditableText
              as="h2"
              value={site.get("faq.stillHaveQuestions.heading", "STILL HAVE QUESTIONS?")}
              editMode={site.editMode}
              onSave={(value) => site.save("faq.stillHaveQuestions.heading", value)}
              className="block text-[22px] font-light tracking-widest"
            />
            <EditableText
              as="p"
              value={site.get(
                "faq.stillHaveQuestions.body",
                "Our team can help you understand the filing process and the information required for your application."
              )}
              editMode={site.editMode}
              multiline
              onSave={(value) => site.save("faq.stillHaveQuestions.body", value)}
              className="mx-auto mt-4 block max-w-2xl text-sm leading-relaxed text-gray-600"
            />
            <a
              href="https://www.dantz-ip.com/en/contact-and-make-appointment"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-block border border-dantzBlue px-6 py-2 text-xs tracking-widest text-dantzBlue hover:bg-dantzBlue hover:text-white"
            >
              CONTACT US
            </a>
          </div>
        </div>
      </section>

      <Footer
        editable={site.editMode}
        email={site.get("faq.footer.email", "info@xpress-ip.com")}
        tagline={site.get("faq.footer.tagline", "EUROPEAN IP FILING, MADE SIMPLE.")}
        taglineSub={site.get("faq.footer.taglineSub", "Patents · Trademarks · Designs")}
        onSaveEmail={(value) => site.save("faq.footer.email", value)}
        onSaveTagline={(value) => site.save("faq.footer.tagline", value)}
        onSaveTaglineSub={(value) => site.save("faq.footer.taglineSub", value)}
      />
    </div>
  )
}
